/**
 * ProjectSettingsModal - プロジェクト設定モーダル
 *
 * GanttChartV2用のプロジェクト設定モーダル
 * - 基本情報（プロジェクト名、色、説明）
 * - マイルストーン管理
 * - チーム設定
 * - 詳細設定（タスク順序同期、プロジェクト削除）※編集モードのみ
 */

import { useState, useEffect } from 'react'
import { TEAMS_ENABLED, LINK_SHARING_ENABLED } from '../config/features'
import ShareLinkSection from './ShareLinkSection'
import {
  X,
  Settings,
  Flag,
  Calendar,
  Trash2,
  Users,
  AlertTriangle,
  Plus,
  ChevronRight,
  RefreshCw,
  Check,
  Share2,
  Loader2,
} from 'lucide-react'
import type { Project } from '../lib/supabase'
import { getTeams, addProjectToTeam, removeProjectFromTeam } from '../services/teamService'
import type { Team } from '../types/team'

// マイルストーン型（GanttChartV2と共有）
export type Milestone = {
  id: string
  projectId: string
  templateId: string | null
  title: string
  startDate: string
  endDate: string | null
  iconType: 'emoji' | 'lucide'
  iconValue: string
  color: string
  sortOrder: number
}

// マイルストーンテンプレート型
export type MilestoneTemplate = {
  id: string
  teamId: string
  title: string
  iconType: 'emoji' | 'lucide'
  iconValue: string
  color: string
  sortOrder: number
}

// プロジェクトの色オプション
const PROJECT_COLORS = [
  '#3B82F6', // blue
  '#22C55E', // green
  '#EAB308', // yellow
  '#F97316', // orange
  '#EF4444', // red
  '#8B5CF6', // purple
  '#06B6D4', // cyan
  '#6B7280', // gray
]

interface ProjectSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  project: Project | null  // nullの場合は新規作成モード
  milestones: Milestone[]
  templates: MilestoneTemplate[]
  onUpdateProject: (projectId: string, updates: Partial<Project>) => void
  onDeleteProject: (projectId: string) => void
  onAddMilestone: (milestone: Partial<Milestone>) => void
  onUpdateMilestone: (milestoneId: string, updates: Partial<Milestone>) => void
  onDeleteMilestone: (milestoneId: string) => void
  onOpenTemplateManager: () => void
  onSyncTaskToGantt: (projectId: string) => void
  onSyncGanttToTask: (projectId: string) => void
  // チーム関連
  teamName?: string
  onTeamChange?: () => Promise<void>
  // 新規作成関連
  onCreateProject?: (data: {
    name: string
    color: string
    description: string
    teamId: string | null
    pendingMilestones: Array<Partial<Milestone>>
  }) => Promise<void>
}

export default function ProjectSettingsModal({
  isOpen,
  onClose,
  project,
  milestones,
  templates,
  onUpdateProject,
  onDeleteProject,
  onAddMilestone,
  onUpdateMilestone,
  onDeleteMilestone,
  onOpenTemplateManager,
  onSyncTaskToGantt,
  onSyncGanttToTask,
  teamName,
  onTeamChange,
  onCreateProject,
}: ProjectSettingsModalProps) {
  const [projectName, setProjectName] = useState('')
  const [projectColor, setProjectColor] = useState('#3B82F6')
  const [projectDescription, setProjectDescription] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // 新規作成モードかどうか
  const isCreateMode = !project
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAddMilestone, setShowAddMilestone] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [milestoneStartDate, setMilestoneStartDate] = useState('')
  const [milestoneEndDate, setMilestoneEndDate] = useState('')
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null)
  const [editingMilestoneStartDate, setEditingMilestoneStartDate] = useState('')
  const [editingMilestoneEndDate, setEditingMilestoneEndDate] = useState('')
  // チーム選択関連
  const [teams, setTeams] = useState<Team[]>([])
  const [teamsLoading, setTeamsLoading] = useState(false)
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)
  const [teamSubmitting, setTeamSubmitting] = useState(false)
  const [teamError, setTeamError] = useState<string | null>(null)
  const [showTeamSelector, setShowTeamSelector] = useState(false)
  // 新規作成モード用: 一時保存マイルストーン
  const [pendingMilestones, setPendingMilestones] = useState<Array<Partial<Milestone> & { tempId: string }>>([])
  const [pendingEditingId, setPendingEditingId] = useState<string | null>(null)
  const [pendingEditingStartDate, setPendingEditingStartDate] = useState('')
  const [pendingEditingEndDate, setPendingEditingEndDate] = useState('')

  // チーム一覧を取得
  const loadTeams = async () => {
    try {
      setTeamsLoading(true)
      const data = await getTeams()
      setTeams(data)
    } catch (err) {
      console.error('Error loading teams:', err)
      setTeamError('チームの読み込みに失敗しました')
    } finally {
      setTeamsLoading(false)
    }
  }

  // プロジェクトが変わったらフォームをリセット
  useEffect(() => {
    if (project) {
      // 編集モード
      setProjectName(project.name || '')
      setProjectColor(project.color || '#3B82F6')
      setProjectDescription(project.description || '')
      setSelectedTeamId(project.team_id || null)
    } else {
      // 新規作成モード
      setProjectName('')
      setProjectColor(PROJECT_COLORS[Math.floor(Math.random() * PROJECT_COLORS.length)])
      setProjectDescription('')
      setSelectedTeamId(null)
    }
    setShowDeleteConfirm(false)
    setShowAddMilestone(false)
    setSelectedTemplateId(null)
    setMilestoneStartDate('')
    setMilestoneEndDate('')
    setEditingMilestoneId(null)
    setShowTeamSelector(false)
    setTeamError(null)
    setIsCreating(false)
    // 新規作成用ステートのリセット
    setPendingMilestones([])
    setPendingEditingId(null)
    setPendingEditingStartDate('')
    setPendingEditingEndDate('')
  }, [project, isOpen])

  // チームセレクターを開いたらチーム一覧を取得
  useEffect(() => {
    if (showTeamSelector && teams.length === 0) {
      loadTeams()
    }
  }, [showTeamSelector])

  // 新規作成モードでモーダルを開いたらチーム一覧を取得
  useEffect(() => {
    if (isOpen && isCreateMode && teams.length === 0) {
      loadTeams()
    }
  }, [isOpen, isCreateMode])

  if (!isOpen) return null

  const projectMilestones = project ? milestones.filter(m => m.projectId === project.id) : []

  // 新規作成処理
  const handleCreateProject = async () => {
    if (!projectName.trim() || !onCreateProject) return
    setIsCreating(true)
    try {
      await onCreateProject({
        name: projectName.trim(),
        color: projectColor,
        description: projectDescription,
        teamId: selectedTeamId,
        pendingMilestones: pendingMilestones,
      })
      onClose()
    } catch (error) {
      console.error('Failed to create project:', error)
    } finally {
      setIsCreating(false)
    }
  }

  // 新規作成モード: 一時マイルストーン追加
  const handleAddPendingMilestone = () => {
    if (!selectedTemplateId || !milestoneStartDate) return

    const template = templates.find(t => t.id === selectedTemplateId)
    if (!template) return

    const tempId = `temp-${Date.now()}`
    setPendingMilestones(prev => [...prev, {
      tempId,
      templateId: template.id,
      title: template.title,
      startDate: milestoneStartDate,
      endDate: milestoneEndDate || null,
      iconType: template.iconType,
      iconValue: template.iconValue,
      color: template.color,
      sortOrder: prev.length,
    }])

    setShowAddMilestone(false)
    setSelectedTemplateId(null)
    setMilestoneStartDate('')
    setMilestoneEndDate('')
  }

  // 新規作成モード: 一時マイルストーン削除
  const handleDeletePendingMilestone = (tempId: string) => {
    if (confirm('このマイルストーンを削除しますか？')) {
      setPendingMilestones(prev => prev.filter(m => m.tempId !== tempId))
    }
  }

  // 新規作成モード: 一時マイルストーン編集開始
  const startEditingPendingMilestone = (milestone: Partial<Milestone> & { tempId: string }) => {
    setPendingEditingId(milestone.tempId)
    setPendingEditingStartDate(milestone.startDate || '')
    setPendingEditingEndDate(milestone.endDate || '')
  }

  // 新規作成モード: 一時マイルストーン日付保存
  const handleSavePendingMilestoneDate = () => {
    if (!pendingEditingId || !pendingEditingStartDate) return
    setPendingMilestones(prev => prev.map(m =>
      m.tempId === pendingEditingId
        ? { ...m, startDate: pendingEditingStartDate, endDate: pendingEditingEndDate || null }
        : m
    ))
    setPendingEditingId(null)
    setPendingEditingStartDate('')
    setPendingEditingEndDate('')
  }

  // 新規作成モード: 一時マイルストーン編集キャンセル
  const cancelEditingPendingMilestone = () => {
    setPendingEditingId(null)
    setPendingEditingStartDate('')
    setPendingEditingEndDate('')
  }

  const handleSaveBasicInfo = () => {
    if (project) {
      onUpdateProject(project.id, {
        name: projectName,
        color: projectColor,
        description: projectDescription,
      })
    }
  }

  const handleAddMilestone = () => {
    if (!project || !selectedTemplateId || !milestoneStartDate) return

    const template = templates.find(t => t.id === selectedTemplateId)
    if (!template) return

    onAddMilestone({
      projectId: project.id,
      templateId: template.id,
      title: template.title,
      startDate: milestoneStartDate,
      endDate: milestoneEndDate || null,
      iconType: template.iconType,
      iconValue: template.iconValue,
      color: template.color,
      sortOrder: projectMilestones.length,
    })

    setShowAddMilestone(false)
    setSelectedTemplateId(null)
    setMilestoneStartDate('')
    setMilestoneEndDate('')
  }

  const handleDeleteMilestone = (milestoneId: string) => {
    if (confirm('このマイルストーンを削除しますか？')) {
      onDeleteMilestone(milestoneId)
    }
  }

  const startEditingMilestone = (milestone: Milestone) => {
    setEditingMilestoneId(milestone.id)
    setEditingMilestoneStartDate(milestone.startDate)
    setEditingMilestoneEndDate(milestone.endDate || '')
  }

  const handleSaveMilestoneDate = () => {
    if (!editingMilestoneId || !editingMilestoneStartDate) return
    onUpdateMilestone(editingMilestoneId, {
      startDate: editingMilestoneStartDate,
      endDate: editingMilestoneEndDate || null,
    })
    setEditingMilestoneId(null)
    setEditingMilestoneStartDate('')
    setEditingMilestoneEndDate('')
  }

  const cancelEditingMilestone = () => {
    setEditingMilestoneId(null)
    setEditingMilestoneStartDate('')
    setEditingMilestoneEndDate('')
  }

  const handleDeleteProject = () => {
    if (!project) return
    onDeleteProject(project.id)
    onClose()
  }

  // チーム変更を適用
  const handleTeamChange = async () => {
    if (!project) return
    if (selectedTeamId === project.team_id) {
      setShowTeamSelector(false)
      return
    }

    setTeamSubmitting(true)
    setTeamError(null)

    try {
      // 既存のチームから解除（必要な場合）
      if (project.team_id) {
        await removeProjectFromTeam(project.id)
      }

      // 新しいチームに追加（チームが選択されている場合）
      if (selectedTeamId) {
        await addProjectToTeam(project.id, selectedTeamId)
      }

      // 親コンポーネントに通知してデータを再取得
      if (onTeamChange) {
        await onTeamChange()
      }

      setShowTeamSelector(false)
    } catch (err) {
      console.error('Error changing team:', err)
      const errorMessage = err instanceof Error ? err.message : 'チームの変更に失敗しました'
      if (errorMessage.includes('duplicate key') || errorMessage.includes('23505')) {
        setTeamError('このプロジェクトは既にこのチームに共有されています')
      } else {
        setTeamError(errorMessage)
      }
    } finally {
      setTeamSubmitting(false)
    }
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getMonth() + 1}/${d.getDate()}`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            {isCreateMode ? (
              <Plus className="w-5 h-5 text-indigo-600" />
            ) : (
              <Settings className="w-5 h-5 text-gray-600" />
            )}
            <h3 className="font-bold text-lg truncate">
              {isCreateMode ? '新規プロジェクト' : 'プロジェクト設定'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* コンテンツ - スクロール可能な単一ビュー */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* プロジェクト名 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              プロジェクト名{isCreateMode && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onBlur={isCreateMode ? undefined : handleSaveBasicInfo}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder={isCreateMode ? '例: 動画A' : undefined}
              autoFocus={isCreateMode}
            />
          </div>

          {/* バーの色 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              バーの色
            </label>
            <div className="flex flex-wrap gap-2">
              {PROJECT_COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    setProjectColor(color)
                    if (!isCreateMode && project) onUpdateProject(project.id, { color })
                  }}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    projectColor === color
                      ? 'border-gray-800 scale-110 ring-2 ring-offset-2'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color, '--tw-ring-color': color } as React.CSSProperties}
                />
              ))}
            </div>
          </div>

          {/* ホバー時の説明 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isCreateMode ? '説明（任意）' : 'ホバー時の説明'}
            </label>
            <textarea
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              onBlur={isCreateMode ? undefined : handleSaveBasicInfo}
              rows={2}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              placeholder="プロジェクトの補足情報..."
            />
          </div>

          {/* 区切り線 */}
          <hr className="border-gray-200" />

          {/* マイルストーン */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flag className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium text-gray-700">マイルストーン</span>
                {isCreateMode && (
                  <span className="text-xs text-gray-400">（任意）</span>
                )}
              </div>
              {!isCreateMode && (
                <button
                  onClick={onOpenTemplateManager}
                  className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                  <Settings className="w-3 h-3" />
                  テンプレート管理
                </button>
              )}
            </div>

            {/* マイルストーン一覧（新規作成モード） */}
            {isCreateMode && (
              <div className="space-y-2 mb-3">
                {pendingMilestones.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-lg">
                    マイルストーンがありません
                  </div>
                ) : (
                  pendingMilestones.map(milestone => (
                    <div key={milestone.tempId}>
                      {pendingEditingId === milestone.tempId ? (
                        // 日付編集モード
                        <div className="p-2 border rounded-lg bg-indigo-50 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{milestone.iconValue}</span>
                            <span className="text-sm font-medium" style={{ color: milestone.color }}>
                              {milestone.title}
                            </span>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">日付</label>
                            <input
                              type="date"
                              value={pendingEditingStartDate}
                              onChange={(e) => setPendingEditingStartDate(e.target.value)}
                              className="w-full border rounded px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={cancelEditingPendingMilestone}
                              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
                            >
                              キャンセル
                            </button>
                            <button
                              onClick={handleSavePendingMilestoneDate}
                              disabled={!pendingEditingStartDate}
                              className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" />
                              保存
                            </button>
                          </div>
                        </div>
                      ) : (
                        // 表示モード
                        <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50 group">
                          <span className="text-lg">{milestone.iconValue}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate" style={{ color: milestone.color }}>
                              {milestone.title}
                            </div>
                            <div className="text-xs text-gray-400">
                              {milestone.startDate && formatDate(milestone.startDate)}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditingPendingMilestone(milestone)}
                              className="p-1 hover:bg-gray-200 rounded"
                              title="日付を編集"
                            >
                              <Calendar className="w-4 h-4 text-gray-500" />
                            </button>
                            <button
                              onClick={() => handleDeletePendingMilestone(milestone.tempId)}
                              className="p-1 hover:bg-red-100 rounded"
                              title="削除"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* マイルストーン一覧（編集モード） */}
            {!isCreateMode && (
              <div className="space-y-2 mb-3">
                {projectMilestones.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-lg">
                    マイルストーンがありません
                  </div>
                ) : (
                  projectMilestones.map(milestone => (
                    <div key={milestone.id}>
                      {editingMilestoneId === milestone.id ? (
                        // 日付編集モード
                        <div className="p-2 border rounded-lg bg-indigo-50 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{milestone.iconValue}</span>
                            <span className="text-sm font-medium" style={{ color: milestone.color }}>
                              {milestone.title}
                            </span>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">日付</label>
                            <input
                              type="date"
                              value={editingMilestoneStartDate}
                              onChange={(e) => setEditingMilestoneStartDate(e.target.value)}
                              className="w-full border rounded px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={cancelEditingMilestone}
                              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded"
                            >
                              キャンセル
                            </button>
                            <button
                              onClick={handleSaveMilestoneDate}
                              disabled={!editingMilestoneStartDate}
                              className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" />
                              保存
                            </button>
                          </div>
                        </div>
                      ) : (
                        // 表示モード
                        <div className="flex items-center gap-2 p-2 border rounded-lg hover:bg-gray-50 group">
                          <span className="text-lg">{milestone.iconValue}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate" style={{ color: milestone.color }}>
                              {milestone.title}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatDate(milestone.startDate)}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditingMilestone(milestone)}
                              className="p-1 hover:bg-gray-200 rounded"
                              title="日付を編集"
                            >
                              <Calendar className="w-4 h-4 text-gray-500" />
                            </button>
                            <button
                              onClick={() => handleDeleteMilestone(milestone.id)}
                              className="p-1 hover:bg-red-100 rounded"
                              title="削除"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* マイルストーン追加（テンプレートがある場合のみ表示） */}
            {templates.length > 0 && (
              <>
                {!showAddMilestone ? (
                  <button
                    onClick={() => setShowAddMilestone(true)}
                    className="w-full flex items-center justify-center gap-2 p-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>マイルストーンを追加</span>
                  </button>
                ) : (
                  <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
                    <div className="text-sm font-medium text-gray-700">マイルストーンを追加</div>

                    {/* テンプレート選択 */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">テンプレート</label>
                      <select
                        value={selectedTemplateId || ''}
                        onChange={(e) => setSelectedTemplateId(e.target.value || null)}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                      >
                        <option value="">選択してください</option>
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.iconValue} {t.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 日付 */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">日付</label>
                      <input
                        type="date"
                        value={milestoneStartDate}
                        onChange={(e) => setMilestoneStartDate(e.target.value)}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                      />
                    </div>

                    {/* ボタン */}
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setShowAddMilestone(false)
                          setSelectedTemplateId(null)
                          setMilestoneStartDate('')
                        }}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded"
                      >
                        キャンセル
                      </button>
                      <button
                        onClick={isCreateMode ? handleAddPendingMilestone : handleAddMilestone}
                        disabled={!selectedTemplateId || !milestoneStartDate}
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        追加
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* テンプレートがない場合のメッセージ */}
            {templates.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-3 bg-gray-50 rounded-lg">
                {isCreateMode
                  ? 'チームを選択するとマイルストーンを追加できます'
                  : 'テンプレートを作成するとマイルストーンを追加できます'
                }
              </div>
            )}
          </div>

          {/* 区切り線 */}
          <hr className="border-gray-200" />

          {/* タスク順序の同期（編集モードのみ） */}
          {!isCreateMode && project && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <RefreshCw className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">タスク順序の同期</span>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => onSyncTaskToGantt(project.id)}
                  className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📋→📊</span>
                    <span className="text-sm">タスク画面 → ガント</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
                <button
                  onClick={() => onSyncGanttToTask(project.id)}
                  className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📊→📋</span>
                    <span className="text-sm">ガント → タスク画面</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
          )}

          {/* 共有リンク（編集モードのみ・オーナーのみ表示） */}
          {LINK_SHARING_ENABLED && !isCreateMode && project && (
            <ShareLinkSection
              project={project}
              onChanged={onTeamChange}
            />
          )}

          {/* チーム設定（無料・個人向けモードでは非表示） */}
          {TEAMS_ENABLED && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {isCreateMode ? 'チーム（任意）' : 'チーム共有'}
              </span>
            </div>

            {/* 新規作成モード: シンプルなチーム選択 */}
            {isCreateMode && (
              <div className="border rounded-lg p-3 bg-gray-50">
                {teamsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : teams.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    チームがありません<br />
                    <span className="text-xs text-gray-400">個人プロジェクトとして作成されます</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* 個人プロジェクト */}
                    <button
                      type="button"
                      onClick={() => setSelectedTeamId(null)}
                      className={`w-full p-2 rounded-lg border-2 transition-all text-left ${
                        selectedTeamId === null
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <X className="w-4 h-4 text-gray-500" />
                          <span className="text-sm">個人プロジェクト</span>
                        </div>
                        {selectedTeamId === null && (
                          <Check className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    </button>

                    {/* チーム一覧 */}
                    {teams.map((team) => (
                      <button
                        key={team.id}
                        type="button"
                        onClick={() => setSelectedTeamId(team.id)}
                        className={`w-full p-2 rounded-lg border-2 transition-all text-left ${
                          selectedTeamId === team.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            <span className="text-sm">{team.name}</span>
                          </div>
                          {selectedTeamId === team.id && (
                            <Check className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 編集モード: 既存のチーム選択UI */}
            {!isCreateMode && project && (
              <div className="border rounded-lg p-3 bg-gray-50">
                {!showTeamSelector ? (
                  // 通常表示
                  <>
                    <div className="text-sm text-gray-600 mb-2">
                      共有先: <span className="font-medium">{teamName || '個人プロジェクト'}</span>
                    </div>
                    <button
                      onClick={() => setShowTeamSelector(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                      <span>{teamName ? 'チーム変更' : 'チーム共有'}</span>
                    </button>
                  </>
                ) : (
                  // チーム選択モード
                  <div className="space-y-3">
                    {teamError && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                        {teamError}
                      </div>
                    )}

                    {teamsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      </div>
                    ) : teams.length === 0 ? (
                      <div className="text-center py-4 text-sm text-gray-500">
                        チームがありません
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {/* 個人プロジェクトに戻すオプション */}
                        {project.team_id && (
                          <button
                            type="button"
                            onClick={() => setSelectedTeamId(null)}
                            disabled={teamSubmitting}
                            className={`w-full p-2 rounded-lg border-2 transition-all text-left ${
                              selectedTeamId === null
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <X className="w-4 h-4 text-gray-500" />
                                <span className="text-sm">個人プロジェクトに戻す</span>
                              </div>
                              {selectedTeamId === null && (
                                <Check className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                          </button>
                        )}

                        {/* チーム一覧 */}
                        {teams.map((team) => (
                          <button
                            key={team.id}
                            type="button"
                            onClick={() => setSelectedTeamId(team.id)}
                            disabled={teamSubmitting}
                            className={`w-full p-2 rounded-lg border-2 transition-all text-left ${
                              selectedTeamId === team.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-blue-600" />
                                <span className="text-sm">{team.name}</span>
                              </div>
                              {selectedTeamId === team.id && (
                                <Check className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => {
                          setShowTeamSelector(false)
                          setSelectedTeamId(project.team_id || null)
                          setTeamError(null)
                        }}
                        disabled={teamSubmitting}
                        className="flex-1 px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        キャンセル
                      </button>
                      <button
                        onClick={handleTeamChange}
                        disabled={teamSubmitting || selectedTeamId === project.team_id}
                        className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1"
                      >
                        {teamSubmitting ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            処理中...
                          </>
                        ) : (
                          '適用'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* 危険な操作（編集モードのみ） */}
          {!isCreateMode && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-red-600">危険な操作</span>
              </div>
              <div className="border border-red-200 rounded-lg p-3 bg-red-50">
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-600 border border-red-300 rounded hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>プロジェクトを削除</span>
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="text-sm text-red-700">
                      <strong>本当に削除しますか？</strong>
                      <br />
                      プロジェクト内のタスクもすべて削除されます。この操作は取り消せません。
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="flex-1 px-3 py-2 text-sm border rounded bg-white hover:bg-gray-50"
                      >
                        キャンセル
                      </button>
                      <button
                        onClick={handleDeleteProject}
                        className="flex-1 px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        削除する
                      </button>
                    </div>
                  </div>
                )}
                <div className="text-xs text-red-400 mt-2">
                  ※タスクもすべて削除されます
                </div>
              </div>
            </div>
          )}

          {/* 新規作成モードの場合は作成ボタンを表示 */}
          {isCreateMode && (
            <div className="flex gap-2 pt-2 border-t mt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleCreateProject}
                disabled={!projectName.trim() || isCreating}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    作成中...
                  </>
                ) : (
                  '作成'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
